import { observer } from 'mobx-react';
import { Fragment, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import routes from '../../routes/index.routes';
import { IMFPStore } from '../../stores';
import { ask, IRequestInterceptor, IResponseInterceptor } from '@monorepo/tools/src/lib/tools/ask/ask';
import { useUser } from '../../hooks/apis/use-user-get';
import { BarLoader } from '@monorepo/base/src/components/bar-loader/bar-loader';

interface IRequireAuth {
	children: ReactNode;
}

/**
 * Redirect users to login page in case of user not authenticated
 */
export const RequireAuth = observer((props: IRequireAuth) => {
	const { children } = props;
	const { authStore, userStore } = useStores<IMFPStore>();
	const token = authStore.getToken();
	const location = useLocation();
	const user = userStore.getUser(); // not permissions

	// Must be before requests
	const authInterceptor: IRequestInterceptor = {
		call: (url, requestOptions) => {
			if (token) {
				requestOptions.headers = {
					Authorization: `Bearer ${token}`,
					...requestOptions.headers,
				};
			}
		},
	};
	ask.interceptors.requests.set('Auth', authInterceptor);
	const updateTokenInterceptor: IResponseInterceptor = {
		success: response => {
			const userToken = response.headers.get('User-Token');
			if (!userToken) {
				return null;
			}
			authStore.setToken(userToken);
		},
	};
	ask.interceptors.responses.set('UpdateToken', updateTokenInterceptor);

	const { isLoading: isUserLoading, isSuccess: isUserSuccess, error } = useUser();

	const authToken = authStore.getToken();
	if (!authToken || !user?.id || !user?.isVerified() || !user.hasAccounts()) {
		userStore.removeUser();
		authStore.removeToken();
		// Redirect them to the /login page, but save the current location they were
		// trying to go to when they were redirected. This allows us to send them
		// along to that page after they login, which is a nicer user experience
		// than dropping them off on the home page.
		return <Navigate to={routes.auth.login()} state={{ from: location }} replace />;
	}

	if (!isUserLoading && isUserSuccess) {
		// User finished loading
		if (!user?.getDomainPermissions()) {
			user?.setDomainPermissions({});
			return <Navigate to={routes.general.home()} state={{ from: location }} replace />;
		}
		return <Fragment>{children}</Fragment>; // The fragment is just for not typescript yelling
	}

	if (error) {
		userStore.removeUser();
		authStore.removeToken();
		return <Navigate to={routes.auth.login()} state={{ from: location }} replace />;
	}

	return <BarLoader is={true} />;
});
